import React from 'react';
import PropTypes from 'prop-types';
import { withFieldProps } from 'hooks/useFieldProps';
import classnames from 'classnames';
import RawHtml from 'common/RawHtml';
import Spinner from 'common/Spinner';
import { withStyles } from 'theme/withStyles';
import checkboxStyles from 'styles/checkbox';
import ErrorMsg from 'common/ErrorMsg';
import Help from 'common/Help';
import Tooltip from 'common/Tooltip';

const styles = (theme) => {
  const { spacing, typography } = theme;
  return {
    desc: {},
    fieldOptionalLabelSuffix: {},
    input: {
      ...checkboxStyles(theme),
      left: 0,
      position: 'absolute',
      top: 0,
    },
    label: {
      ...typography.p,
      '& $desc': {
        '& p': typography.p3,
        ...typography.p3,
        display: 'block',
        marginTop: 5,
      },
      '& $fieldOptionalLabelSuffix': {
        display: 'none',
      },
      '& *': {
        '& ol': {
          listStyle: 'number',
        },
        margin: 0,
        padding: 0,
      },
      '& strong': {
        display: 'inline-block',
      },
      color: typography.p3.color,
      fontSize: 14,
      lineHeight: 'auto',
      verticalAlign: 'middle',
    },
    root: {
      '& .spinner': {
        top: '-2px !important',
      },
      marginTop: spacing.sm,
      paddingLeft: 34,
      position: 'relative',
    },
  };
};

const BooleanBare = ({
  classes,
  error,
  fieldDescr,
  fieldDomId,
  fieldOptionalLabelSuffix,
  help,
  label,
  loading,
  name,
  onChange,
  readOnly,
  required,
  tooltip,
  validate,
  value,
}) => (
  <>
    <div className={classnames('checkbox', 'form-group', { error, loading }, classes.root)}>
      <Spinner loading={loading} />
      <input
        type="checkbox"
        id={fieldDomId}
        aria-describedby={
          classnames({
            [`help-${name}`]: help,
            [`error-${name}`]: error,
          }) || undefined
        }
        name={fieldDomId}
        checked={value || false}
        readOnly={readOnly}
        onChange={(e) => {
          if (!readOnly) {
            onChange(name, e.target.checked);
            validate();
          }
        }}
        disabled={loading}
        className={classes.input}
      />{' '}
      <label htmlFor={fieldDomId} className={classes.label}>
        <RawHtml type="span">{label}</RawHtml>
        {!required && (
          <RawHtml type="small" className={classes.fieldOptionalLabelSuffix}>
            {` ${fieldOptionalLabelSuffix || '(Optional)'}`}
          </RawHtml>
        )}
        {fieldDescr && (
          <RawHtml type="small" className={classes.desc}>
            {fieldDescr}
          </RawHtml>
        )}
      </label>
      <Tooltip tooltip={tooltip} labeledBy={`label-${fieldDomId}`} handleYOffset={5} />
      <Help help={help} id={`help-${name}`} />
    </div>

    <ErrorMsg name={name} />
  </>
);

BooleanBare.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  error: PropTypes.bool.isRequired,
  fieldDescr: PropTypes.string.isRequired,
  fieldDomId: PropTypes.string.isRequired,
  fieldOptionalLabelSuffix: PropTypes.string.isRequired,
  help: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool.isRequired,
  required: PropTypes.bool.isRequired,
  tooltip: PropTypes.string.isRequired,
  validate: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
};

const BooleanStyled = withStyles(styles)(BooleanBare);

const mapStateToProps = ({ resources }) => ({
  fieldOptionalLabelSuffix: resources.fieldOptionalLabelSuffix ?? '',
});

export default withFieldProps(mapStateToProps)(BooleanStyled);
